import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, Shield, X } from 'lucide-react';
import { Button } from '../ui';

interface PasswordResetPromptModalProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onConfirm: () => void;
}

const PasswordResetPromptModal: React.FC<PasswordResetPromptModalProps> = ({
  open,
  email,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('login');

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="password-reset-modal-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 rounded-lg"
          aria-label={t('noPassword.modal.cancel')}
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-4 pr-8">
          <div className="flex-shrink-0 w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
            <KeyRound className="text-amber-700" size={22} />
          </div>
          <h2 id="password-reset-modal-title" className="text-xl font-semibold text-gray-900">
            {t('noPassword.modal.title')}
          </h2>
        </div>

        <p className="text-gray-600 mb-4">{t('noPassword.modal.intro')}</p>

        <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 mb-4 space-y-3">
          <div className="flex gap-3">
            <Shield className="text-custom-primary flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-gray-700">{t('noPassword.modal.reasonSecurity')}</p>
          </div>
          <p className="text-sm text-gray-700">{t('noPassword.modal.reasonGoogle')}</p>
          {email ? (
            <p className="text-sm text-gray-600">
              {t('noPassword.modal.accountLabel')}{' '}
              <strong className="text-gray-900">{email}</strong>
            </p>
          ) : null}
        </div>

        <p className="text-sm font-medium text-gray-800 mb-6">{t('noPassword.modal.question')}</p>

        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <Button type="button" variant="outline" className="w-full sm:flex-1" onClick={onClose}>
            {t('noPassword.modal.cancel')}
          </Button>
          <Button type="button" variant="primary" className="w-full sm:flex-1" onClick={onConfirm}>
            {t('noPassword.modal.confirm')}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PasswordResetPromptModal;
